import { useCallback, useState } from 'react';

import clsx from 'clsx';

import {
  Button,
  Column,
  EllipsisWithTooltip,
  Icons,
  PreviewSnippet,
  Tooltip,
  useDrawer,
  useDrawerInstance,
  notify,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  Webhook,
  WebhookStatus,
  SlackConfiguration,
  SlackStatus,
} from '@cast/types';

import { useAbility } from '@cast/shared-ability-data-access';

import { AlertFormSubdrawer } from './form';
import { WebhookStatusChip } from '../_components';
import { AddAlertDrawer } from './AddAlertDrawer';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  SearchableTable,
  useSearchContext,
} from '@cast/shared-search-ui';

const TooltipProps = {
  arrow: true,
  PopperProps: {
    sx: {
      '& .MuiTooltip-tooltip': {
        backgroundColor: tokens.theme.bg.structural.surfaceInverse,
        maxWidth: 420,
      },
    },
  },
};

type AlertConfig = Webhook | SlackConfiguration;

const isSlackConfiguration = (
  config: AlertConfig
): config is SlackConfiguration => {
  return 'channels' in config;
};

type AlertChipProps = { alert: AlertConfig };
const AlertChipWithTooltip = ({ alert }: AlertChipProps) => {
  if (
    alert.status === WebhookStatus.FAILED ||
    alert.status === SlackStatus.NOT_OK ||
    alert.status === SlackStatus.DEGRADED
  ) {
    const isError =
      alert.status === WebhookStatus.FAILED ||
      alert.status === SlackStatus.NOT_OK;

    return (
      <Tooltip
        {...TooltipProps}
        title={
          <>
            <Typography variant="P12R">
              {isError
                ? 'Cannot connect due to recurring issue:'
                : 'Degraded delivery'}
            </Typography>
            {isError ? (
              <PreviewSnippet
                code={alert.error!}
                errorHighlight={isError}
                onCopy={() => notify.success('Copied to clipboard')}
                p="16px"
              />
            ) : null}
          </>
        }
        sx={{ '.MuiTooltip-tooltip': { borderRadius: '4px' } }}
      >
        <div>
          <WebhookStatusChip status={alert.status} noText size="sm" />
        </div>
      </Tooltip>
    );
  }
  return <WebhookStatusChip status={alert.status} noText size="sm" />;
};

export const AlertsList = () => {
  const ctx = useSearchContext();
  const { open } = useDrawer();
  const { close } = useDrawerInstance();
  const instance = useDrawerInstance();
  const [activeAlert, setActiveAlert] = useState<AlertConfig>();
  const ability = useAbility();

  const onAddAlert = useCallback(() => {
    close({
      onComplete: () => {
        open({
          component: AddAlertDrawer,
          props: {},
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <SearchableTable
      fillParentHeight
      isFetching={ctx?.isLoading && !!ctx?.data?.length}
      rowKey={(row: AlertConfig) => row.id!}
      onRowClick={(_, { row }) => {
        setActiveAlert(row);
        const props = isSlackConfiguration(row)
          ? { slackConfigurationId: row.id }
          : { webhook: row };
        open({
          component: AlertFormSubdrawer,
          props: {
            ...props,
            onClose: () => {
              setActiveAlert(undefined);
            },
          },
          options: { parentId: instance.id },
        });
      }}
      components={{
        noData: (
          <NoData
            actions={
              <Button
                onClick={onAddAlert}
                disabled={ability.cannot('create', 'OrganizationWebHooks')}
                size="sm"
                startIcon={<Icons.Plus size={16} />}
                testId="add-alert-button"
              >
                Create alert
              </Button>
            }
            entity="alert"
          />
        ),
        failed: (
          <FailedToLoad
            title="Failed to load alerts"
            refresh={ctx?.refetch}
            sx={{ px: 32, pt: 108 }}
          />
        ),
      }}
      testId="alerts-table"
    >
      <Column
        id="name"
        header="Name"
        minWidth={150}
        renderCell={(alert: AlertConfig) => {
          return (
            <EllipsisWithTooltip
              TypographyProps={{
                variant: 'P12M',
                color: tokens.theme.fg.action.primary,
                noWrap: true,
              }}
              TooltipProps={TooltipProps}
            >
              {alert.name}
            </EllipsisWithTooltip>
          );
        }}
        cellWrapperSx={{ pl: 32 }}
        sortable
      />
      <Column
        id="delivery"
        header="Delivery"
        minWidth={130}
        accessor={(alert: AlertConfig) =>
          isSlackConfiguration(alert) ? 'Slack' : 'Webhook'
        }
        renderCell={(alert: AlertConfig) => {
          const deliveryText = isSlackConfiguration(alert)
            ? 'Slack'
            : 'Webhook';
          return (
            <EllipsisWithTooltip
              TypographyProps={{
                variant: 'P12M',
                noWrap: true,
              }}
              TooltipProps={TooltipProps}
            >
              {deliveryText}
            </EllipsisWithTooltip>
          );
        }}
        cellWrapperSx={{ pl: 25 }}
        sortable
      />
      <Column
        id="status"
        header="Status"
        minWidth={100}
        renderCell={(alert: AlertConfig) => {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              pr={8}
            >
              <AlertChipWithTooltip alert={alert} />
              <Icons.CaretLeft
                className={clsx(activeAlert?.id === alert.id && 'alert-active')}
                style={{
                  transition: 'transform 600ms',
                  transform: 'rotate(180deg)',
                  ...(activeAlert?.id === alert.id
                    ? { transform: 'rotate(0deg)' }
                    : {}),
                }}
              />
            </Stack>
          );
        }}
        cellWrapperSx={{ pr: 15 }}
      />
    </SearchableTable>
  );
};
